import * as React from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import isEmpty from 'lodash/isEmpty'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import InfiniteScrollerList from 'modules/core/components/InfiniteScrollerList'
import type { ListSizeType } from 'modules/core/components/InfiniteScrollerList'
import createComputed from 'modules/core/hooks/createComputed'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import { Collection } from 'modules/core/lib/mobx-rest'
import { matchText } from 'modules/core/lib/stringUtils'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'

import Blank from './Blank'
import SearchBar from './SearchBar'
import getFilterValue from './_filters/getFilterValue'

interface Props {
  autofocus?: boolean
  employees: Employee[]
  filtersForm: FiltersForm
  listSize?: ListSizeType
  renderEmployeeListItem: (employee: Employee) => React.ReactElement<any> | null | undefined
  setChildPopoverOpen?: React.Dispatch<React.SetStateAction<boolean>>
}

const useSearchFilter = createComputed((search: string, employees: Employee[]) =>
  employees.filter((employee) => {
    if (isEmpty(search)) return true
    const userId = employee.attributes.access_id
    const user = users.get(userId)
    if (!user) {
      throw Error(`Error getting users with id = ${userId}`)
    }
    return matchText(
      search,
      getFullDisplayName({
        firstName: user.attributes.first_name,
        lastName: user.attributes.last_name,
        canSharePreferredName: employee.policy.can('sharePreferredName'),
        preferredName: employee.attributes.preferred_name,
      })
    )
  })
)

const ListEmployees = ({
  autofocus,
  employees,
  filtersForm,
  renderEmployeeListItem,
  listSize,
  setChildPopoverOpen,
}: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const {
    filteredModels: filteredEmployees,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Employee>(getFilterValue, LOCAL_STORAGE_KEYS.employeesFilters)

  const employeesToShow = useSearchFilter(debouncedSearch, filteredEmployees).get()
  React.useEffect(() => {
    onApplyFilters(employees) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees])

  function renderEmployees(employees: Collection<Employee> | Employee[]) {
    if (employees.length === 0) {
      return <Blank />
    }

    return employees.map(renderEmployeeListItem)
  }

  return (
    <BoxList>
      <SearchBar
        autofocus={autofocus}
        filtersForm={filtersForm}
        onSearch={onSearch}
        search={search}
        onAddFilter={onAddFilter}
        onRemoveFilter={onRemoveFilter}
        onApplyFilters={() => onApplyFilters(employees)}
        onCancel={onCancel}
        filters={filters}
        logicOperator={logicOperator}
        setChildPopoverOpen={setChildPopoverOpen}
      />
      {employeesToShow.length && (
        <InfiniteScrollerList
          useContainerWrapper
          items={employeesToShow}
          initialBatchSize={100}
          listSize={listSize}
        >
          {({ items }) => renderEmployees(items)}
        </InfiniteScrollerList>
      )}
    </BoxList>
  )
}

/**
 * @deprecated This component is deprecated and should not be used in new code. It is considered
 *             to be harmful because it is coupled to the mobx-rest collections. Using this component
 *             will highly impact the performance of your page.
 *             Please, consider using GraphQL instead.
 */
export default observer(ListEmployees)
